// A minimal zip reader, for the Capital on Tap export.
//
// The zip is unpacked HERE, in the browser, rather than being sent to the Worker whole.
// Nineteen megabytes for ten days works fine in a Worker until the month somebody
// exports sixty, and unpacking client-side also means the upload can show progress and
// survive one bad file instead of failing the lot.
//
// No library: the browser has an inflater already (DecompressionStream), and the rest is
// reading a few fixed-width headers. A dependency added for sixty lines of header parsing
// is a dependency to keep up to date for ever.
//
// Deliberately does NOT handle zip64 or encryption. A Capital on Tap export is a few
// dozen small files, and a reader that silently mishandles something it half-supports is
// worse than one that says it cannot.

const EOCD = 0x06054b50;   // end of central directory
const CEN  = 0x02014b50;   // central directory entry
const LOC  = 0x04034b50;   // local file header

export async function readZip(blob) {
  const buf = new Uint8Array(await blob.arrayBuffer());
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);

  // The end-of-directory record is at the end, behind a comment of up to 64KB.
  let eocd = -1;
  for (let i = buf.length - 22; i >= Math.max(0, buf.length - 65558); i--) {
    if (view.getUint32(i, true) === EOCD) { eocd = i; break; }
  }
  if (eocd < 0) throw new Error('That does not look like a zip file.');

  const count = view.getUint16(eocd + 10, true);
  const cdOffset = view.getUint32(eocd + 16, true);
  if (count === 0xffff || cdOffset === 0xffffffff) {
    throw new Error('This zip is in the zip64 format, which this reader does not handle. Unzip it and upload the CSV and receipts separately.');
  }

  const entries = [];
  let p = cdOffset;
  for (let i = 0; i < count; i++) {
    if (view.getUint32(p, true) !== CEN) break;
    const method = view.getUint16(p + 10, true);
    const compressedSize = view.getUint32(p + 20, true);
    const nameLen = view.getUint16(p + 28, true);
    const extraLen = view.getUint16(p + 30, true);
    const commentLen = view.getUint16(p + 32, true);
    const localOffset = view.getUint32(p + 42, true);
    const name = new TextDecoder().decode(buf.subarray(p + 46, p + 46 + nameLen));
    entries.push({ name, method, compressedSize, localOffset });
    p += 46 + nameLen + extraLen + commentLen;
  }

  // Directory entries are not files, and macOS puts its own metadata in every zip.
  const files = entries.filter(e =>
    !e.name.endsWith('/') && !e.name.startsWith('__MACOSX') && !e.name.split('/').pop().startsWith('.'));

  return {
    names: files.map(f => f.name),
    // Read lazily: one entry at a time keeps a large export off the heap all at once.
    async read(name) {
      const e = files.find(f => f.name === name);
      if (!e) throw new Error(`${name} is not in the zip.`);
      // The local header repeats the name and extra fields, and its lengths are the ones
      // that count — the central directory's extra field is often a different size.
      const lnameLen = view.getUint16(e.localOffset + 26, true);
      const lextraLen = view.getUint16(e.localOffset + 28, true);
      const start = e.localOffset + 30 + lnameLen + lextraLen;
      const raw = buf.subarray(start, start + e.compressedSize);
      if (e.method === 0) return raw.slice();            // stored
      if (e.method !== 8) throw new Error(`${name} uses an unsupported compression method (${e.method}).`);
      const stream = new Blob([raw]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
      return new Uint8Array(await new Response(stream).arrayBuffer());
    },
  };
}

// The export's own CSV. Quoted fields, because a merchant name can contain a comma —
// "GITHUB, INC." does — and splitting on commas would shift every column after it.
export function parseCsv(text) {
  const rows = [];
  let row = [], cell = '', quoted = false;
  const s = String(text).replace(/^﻿/, '');
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (quoted) {
      if (ch === '"' && s[i + 1] === '"') { cell += '"'; i++; continue; }
      if (ch === '"') { quoted = false; continue; }
      cell += ch;
      continue;
    }
    if (ch === '"') { quoted = true; continue; }
    if (ch === ',') { row.push(cell); cell = ''; continue; }
    if (ch === '\r') continue;
    if (ch === '\n') { row.push(cell); rows.push(row); row = []; cell = ''; continue; }
    cell += ch;
  }
  if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
  const nonEmpty = rows.filter(r => r.some(c => String(c).trim() !== ''));
  if (!nonEmpty.length) return [];
  const head = nonEmpty[0].map(h => h.trim());
  return nonEmpty.slice(1).map(r => Object.fromEntries(head.map((h, i) => [h, (r[i] ?? '').trim()])));
}
